// Ticket komment-szál (belső nézet) — a detail-oldal külön szekciója. A `ticketComments` sorokat időrendben
// (createdAt ASC, a szerver rendezi) listázza; a BELSŐ (internal) jegyzetet és a KÜLSŐ (external, partnerrel
// megosztott) választ vizuálisan megkülönbözteti (kind-badge + halvány háttér a belső jegyzeten). A belső admin
// MINDKÉT kind-et látja. Új képességek (legacy paritás):
//   • Komment-SZERKESZTÉS (SZERZŐ-scope): a szerkesztő gomb CSAK a szerzőnél jelenik meg (session.sub ==
//     authorIdentityId), a mentés If-Match/OCC-vel (`ticketCommentUpdate`); a szerver is ellenőriz (403 idegennél).
//   • Komment-SABLONOK (SHARED katalógus): „Sablon betöltése" dropdown a szövegmezőbe + „Mentés sablonként" dialog.
// Üres komment tiltva. Az e-mail/értesítés KIHAGYVA.
import { type TicketCommentRowSchema } from '@atlas/contracts';
import {
  BookmarkSquareIcon,
  ChatBubbleLeftRightIcon,
  DocumentDuplicateIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  PencilSquareIcon,
} from '@heroicons/react/24/outline';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { type z } from 'zod';

import { toggleReactionList } from './reaction-toggle';

import { DraftStatus } from '@/components/shared/draft-status';
import { EditedMarker } from '@/components/shared/edited-marker';
import { ListErrorState } from '@/components/shared/list-error-state';
import { PersonChip } from '@/components/shared/person-chip';
import { THREAD_TONE_OURS } from '@/components/shared/thread-tone';
import { TicketCommentReactions } from '@/components/tickets/ticket-comment-reactions';
import { type REACTION_CHOICES } from '@/components/tickets/ticket-comment-reactions';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { RichTextContent } from '@/components/ui/rich-text-content';
import { RichTextEditor } from '@/components/ui/rich-text-editor';
import { Segmented } from '@/components/ui/segmented';
import { Skeleton } from '@/components/ui/skeleton';
import { Text } from '@/components/ui/text';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { templateKeys, ticketKeys } from '@/features/tickets/api';
import { TICKET_SECTION } from '@/features/tickets/sections';
import { useDraft } from '@/hooks/use-draft';
import { api } from '@/lib/api';
import { isForbiddenStatus } from '@/lib/error-status';
import { isRichTextBlank, looksLikeStoredHtml } from '@/lib/sanitize-html';
import { useEntityMutation } from '@/lib/use-entity-mutation';
import { cn } from '@/lib/utils';

/**
 * ÜRES-e a komment törzse? MINDKÉT fajta RICH-TEXT (user-döntés: „a belsős jegyzet és a külső
 * válasz is editoros legyen") — tehát a látható szöveg hosszán mérünk, nem a markupon: egy `<p></p>` padding
 * nem számít tartalomnak.
 */
function isBodyBlank(body: string): boolean {
  // a csak képből álló komment NEM üres (közös szabály: `isRichTextBlank`)
  return isRichTextBlank(body);
}

/**
 * A RAGADÓS composer-sáv (összecsukott ÉS kinyitott állapotban): a szál alján ül, átlátszatlan háttérrel.
 *
 * A FELSŐ ELVÁLASZTÓ VONAL **BEHÚZOTT** (`before:inset-x-6`), nem szélig futó `border-t` — a rendszer
 * hajszálvonal-idiómája ez (`DataTable` fejléc/sorok, `CardRows`: `after:inset-x-3`), és a user-lelet
 * is ez volt: „nem érhet ki teljes szélességben". A behúzás a sáv `px-4`-jén TÚL még 8px, tehát
 * a vonal a lenti beviteli mező bal/jobb szélénél is beljebb kezdődik és ér véget.
 */
const STICKY_BAR = cn(
  'bg-background sticky bottom-0 z-10 -mx-4 px-4 py-2.5',
  // NINCS `relative` a hajszálvonal mellett! A `cn` (tailwind-merge) a `sticky`-t és a `relative`-ot
  // UGYANABBA a pozíció-csoportba teszi, és az UTOLSÓT tartja meg — a `relative` tehát KIÜTÖTTE a
  // `sticky`-t, és a composer-sáv csendben visszakerült a szál aljára (user-lelet: „ugyanúgy
  // le kell görgetni"). Nem is kell: a `sticky` elem MAGA is containing block az `absolute`
  // leszármazottnak, tehát a `before:absolute` vonal hozzá igazodik.
  "before:pointer-events-none before:absolute before:inset-x-6 before:top-0 before:h-px before:bg-[color:var(--table-hairline)] before:content-['']",
);

type TicketComment = z.infer<typeof TicketCommentRowSchema>;

/**
 * OPTIMISTA cache-frissítés egy reakció-toggle-re: a `ticketComments` lista-válaszban az adott komment
 * reakció-sávját írja át úgy, ahogy a szerver is fogja.
 *
 * MIÉRT ITT, ÉS NEM A KOMPONENSBEN: tiszta függvény → tesztelhető a React-fa nélkül, és a szabály (a saját
 * reakció be/ki, a darabszám ±1, üres emoji-csoport eltűnik) EGY helyen van leírva.
 */
export function toggleInCache(prev: unknown, commentId: string, emoji: string): unknown {
  const page = prev as { status?: number; body?: TicketComment[] } | undefined;
  if (page?.status !== 200 || !Array.isArray(page.body)) return prev;
  // A SZABÁLY a közös `toggleReactionList`-ben van (a portál részlet-cache-e ugyanazt használja) — itt csak
  // a lista-alak bejárása marad.
  const body = page.body.map((c) =>
    c.id === commentId ? { ...c, reactions: toggleReactionList(c.reactions, emoji) } : c,
  );
  return { ...page, body };
}
type CommentKind = TicketComment['kind'];

/** ISO → lokalizált dátum+idő (a UI nyelvének megfelelő formátumban). */
function fmtDateTime(iso: string | null, locale: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString(locale);
}

/**
 * Belső/külső kind-választó — a KÖZÖS `Segmented` primitív (ugyanaz a megjelenés, mint a jogosultság-konzol
 * felület-váltóján és az üzenet-írás típus/címzés választóján).
 *
 * KÉT SZEREPBEN áll a lapon:
 *   • a szál FEJLÉCÉBEN (a `counts` meg van adva) — ez váltotta le a „Kommentek" szekció-címet (user-döntés):
 *     egyszerre TAB (mit listázunk) és a composer fajtája, a címkében az adott fajta darabszáma;
 *   • az inline SZERKESZTŐBEN (nincs `counts`) — ott csak a szerkesztett komment fajtáját állítja.
 * Mindkét fajta kap ikont: a komment beszélgetés-buborékot, a belső jegyzet zárat.
 */
function KindPicker({
  value,
  onChange,
  label,
  counts,
}: {
  value: CommentKind;
  onChange: (k: CommentKind) => void;
  label: string;
  /** Fajtánkénti darabszám — csak a fejléc-változatban (a szerkesztőben félrevezető lenne). */
  counts?: Record<CommentKind, number> | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const withCount = (k: CommentKind): string =>
    counts === undefined
      ? t(`tickets.comments.kindTab.${k}`)
      : `${t(`tickets.comments.kindTab.${k}`)} (${String(counts[k])})`;
  return (
    <Segmented
      value={value}
      onChange={(v) => onChange(v === 'external' ? 'external' : 'internal')}
      options={[
        // A FEJLÉC-változat címkéi (`kindTab`) mondat-kezdő nagybetűsek; a thread-badge marad kisbetűs (`kind`).
        {
          value: 'external',
          label: withCount('external'),
          icon: <ChatBubbleLeftRightIcon className="size-3.5" />,
        },
        {
          value: 'internal',
          label: withCount('internal'),
          icon: <LockClosedIcon className="size-3.5" />,
        },
      ]}
      aria-label={label}
    />
  );
}

export interface TicketCommentsProps {
  ticketId: string;
  /** A ticket FIÓKJA — a kommentbe beszúrt kép cél-fiókja (a tenantot a BFF oldja fel belőle). */
  accountId: string;
  /** Komment-küldés/-szerkesztés joga (tickets.manage) — enélkül csak olvasható a szál. */
  canManage: boolean;
}

export function TicketComments({
  ticketId,
  accountId,
  canManage,
}: TicketCommentsProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const locale = i18n.language;

  const [body, setBody] = useState('');
  // DEFAULT: KÜLSŐ válasz (user-döntés) — a napi munkában a partner-válasz a gyakoribb, a belső
  // jegyzet a ritkább eset. Korábban `internal` volt az alap, ami minden válasznál egy plusz kattintás.
  const [kind, setKind] = useState<CommentKind>('external');
  const [saveTplOpen, setSaveTplOpen] = useState(false);

  // AUTOMATIKUS VÁZLAT fajtánként (külső válasz / belső jegyzet külön): a félbehagyott szöveg egy frissítést,
  // fül-váltást vagy fajta-váltást is túlél; küldés vagy elvetés után törlődik. Szerkesztési jog nélkül nincs composer.
  // hely-váltáskor (jegy / fajta) a hely vázlata kerül a szerkesztőbe (a munkamenet késői betöltése nem írja felül
  // a már begépelt szöveget — lásd `useDraft` `onRestore`)
  const draft = useDraft<string>(canManage ? `ticket:${ticketId}:${kind}` : null, {
    onRestore: (value) => setBody(value ?? ''),
  });
  const updateBody = (next: string): void => {
    setBody(next);
    draft.save(next, isBodyBlank(next));
  };

  // A bejelentkezett user identity-je (session.sub) — a komment-szerkesztés SZERZŐ-gate-jéhez.
  const sessionQ = api.session.useQuery(['session'], {});
  const currentSub = sessionQ.data?.status === 200 ? sessionQ.data.body.sub : null;

  const commentsKey = ticketKeys.comments(ticketId);
  const commentsQuery = api.ticketComments.useQuery(commentsKey, { params: { id: ticketId } });
  // A betöltés BUKOTT, és nincs korábbi (sikeres) adat — ezt nem szabad üres szálként mutatni.
  // (Egy sikertelen ÚJRA-töltés a meglévő, sikeres adatot NEM rejti el — a react-query megtartja.)
  const commentsFailed =
    commentsQuery.data?.status !== 200 &&
    (commentsQuery.isError || commentsQuery.data !== undefined);
  const comments: TicketComment[] =
    commentsQuery.data?.status === 200 ? commentsQuery.data.body : [];

  // FAJTÁNKÉNTI darabszám (a tab-címkékhez) és a MEGJELENÍTETT szál. A tab egyszerre szűr és a composer
  // fajtáját állítja (user-döntés: a szekció-címet a tab-sáv váltotta le, darabszámmal) — a
  // darabszám szűrés nélkül félrevezető lett volna („Külső válasz (3)" fölött belső jegyzetek is állnak.)
  const counts: Record<CommentKind, number> = {
    external: comments.filter((c) => c.kind === 'external').length,
    internal: comments.filter((c) => c.kind === 'internal').length,
  };
  const shown = comments.filter((c) => c.kind === kind);

  // Komment-sablonok (SHARED katalógus) — a „Sablon betöltése" dropdownhoz (csak kezelőnek van értelme).
  const templatesQuery = api.ticketCommentTemplates.list.useQuery(templateKeys.list(), {});
  const templates = templatesQuery.data?.status === 200 ? templatesQuery.data.body : [];

  const createMutation = useEntityMutation<{ kind: CommentKind; body: string }>({
    mutationFn: (v) => api.ticketCommentCreate.mutation({ params: { id: ticketId }, body: v }),
    // A lista frissül ÉS a detail (commentCount) invalidálódik (a fejléc-számláló naprakész marad).
    invalidateKeys: [commentsKey, ticketKeys.detail(ticketId)],
    messages: {
      success: t('toast:tickets.commentCreated'),
      conflict: t('toast:tickets.conflict'),
      forbidden: t('toast:tickets.forbidden'),
      validation: t('toast:tickets.validation'),
      error: t('toast:tickets.error'),
    },
  });

  const templateMutation = useEntityMutation<{ title: string; content: string }>({
    mutationFn: (v) => api.ticketCommentTemplates.create.mutation({ body: v }),
    invalidateKeys: [templateKeys.list()],
    messages: {
      success: t('toast:tickets.template.saved'),
      conflict: t('toast:tickets.conflict'),
      forbidden: t('toast:tickets.forbidden'),
      validation: t('toast:tickets.validation'),
      error: t('toast:tickets.error'),
    },
  });

  const blank = isBodyBlank(body);
  const canSend = canManage && !blank && !createMutation.isPending;

  /**
   * A COMPOSER ÖSSZECSUKVA indul, és RAGADÓSAN a szál alján ül (Asana · Jira · Linear · Zendesk-minta).
   *
   * A LELET (user): 50 kommentnél a szerkesztőig hosszan kellett görgetni. A szál-alatti hely
   * ugyanakkor HELYES (a nagyok is így teszik: időrendben olvasunk, a válasz a végén) — a megoldás tehát nem
   * az áthelyezés, hanem hogy a composer MINDIG LÁTHATÓ legyen: egy soros sáv a görgetés-port alján,
   * kattintásra kinyílik (és a fókusz a szerkesztőbe kerül). Így nulla görgetés kell az írás megkezdéséhez,
   * és összecsukva nem vesz el helyet a szálból.
   *
   * Sikeres küldés után VISSZACSUKÓDIK: a sáv visszaáll egy sorra, ami egyben a „elment" visszajelzés is.
   */
  const [composerOpen, setComposerOpen] = useState(false);

  const onSubmit = (e: React.FormEvent): void => {
    e.preventDefault();
    if (!canSend) return;
    // A wire-en mindkét kind `string`-et küld: external → HTML, internal → sima szöveg (nincs contract-változás).
    createMutation.mutate(
      { kind, body: body.trim() },
      {
        onSuccess: () => {
          draft.discard();
          setBody('');
          setComposerOpen(false);
        },
      },
    );
  };

  // Sablon betöltése a szövegmezőbe: üres mezőnél beállít, különben hozzáfűz (a gépelt szöveg nem vész el).
  const loadTemplate = (content: string): void => {
    updateBody(body.trim() === '' ? content : `${body}\n\n${content}`);
  };

  return (
    <section
      id={TICKET_SECTION.comments}
      tabIndex={-1}
      className="scroll-mt-6 space-y-4 focus:outline-none"
    >
      {/* NINCS szekció-CÍM (user-döntés, 2026-08-25): a helyét a BALRA igazított fajta-választó veszi át —
          ikonos címkék az adott fajta darabszámával. A „Kommentek" felirat redundáns volt: a tab-sáv és a
          szál maga is elmondja, mi ez a blokk. A választó egyszerre TAB (mit listázunk) és a composer
          fajtája — a szerkesztés joga nélkül csak szűrő (a composer nem jelenik meg). */}
      {/* A FÜL-SÁV RAGADÓSAN a szál tetején marad. MIÉRT: a fül egyszerre szűrő ÉS a composer FAJTÁJA —
          ha egy 85 kommentes szálon kigörög a képből, a lenti (ragadós) szerkesztőben már nem látszik, hogy
          KOMMENTET vagy BELSŐ JEGYZETET írunk. Ez a kettő nem összekeverhető (az egyik kimegy a partnerhez),
          ezért a kontextus akkor is látszik, amikor a szál közepén állunk. Ugyanez a minta a Zendesk/Jira
          csatorna-választóján. */}
      <div
        className={cn(
          'bg-background sticky top-0 z-10 -mx-4 px-4 pb-2 pt-1',
          // A fül-sáv ALSÓ hajszálvonala is BEHÚZOTT — `relative` NÉLKÜL: az kiütné a `sticky`-t (a
          // tailwind-merge ugyanabba a pozíció-csoportba teszi őket, és az utolsót tartja meg).
          "after:pointer-events-none after:absolute after:inset-x-6 after:bottom-0 after:h-px after:bg-[color:var(--table-hairline)] after:content-['']",
        )}
      >
        <KindPicker
          value={kind}
          // a fajta-váltás NEM dobja el a szöveget: mindkét fajtának saját vázlata van (a váltás után az új fajtáé
          // töltődik be, a régié megmarad)
          onChange={setKind}
          label={t('tickets.comments.kindLabel')}
          counts={counts}
        />
      </div>

      {commentsQuery.isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : commentsFailed ? (
        // A SIKERTELEN betöltés NEM üres szál (user-lelet: Asana-leválasztás után „eltűntek a kommentek" — az adat
        // megvolt, csak a kérés bukott, és a felület „még nincs komment"-et mondott). Hibaállapot + Újra.
        <ListErrorState
          forbidden={isForbiddenStatus(commentsQuery.error?.status)}
          onRetry={() => void commentsQuery.refetch()}
        />
      ) : shown.length === 0 ? (
        <Text variant="secondary" as="p">
          {t('tickets.comments.empty')}
        </Text>
      ) : (
        <ul className="space-y-3">
          {shown.map((c) => (
            <CommentItem
              key={c.id}
              comment={c}
              ticketId={ticketId}
              accountId={accountId}
              locale={locale}
              // SZERZŐ-scope: a szerkesztés CSAK a szerzőnek jelenik meg (a szerver is ellenőriz — 403 idegennél).
              canEdit={canManage && currentSub !== null && currentSub === c.authorIdentityId}
              // A REAGÁLÁS OLVASÓI együttműködés: aki látja a szálat, adhat reakciót (a szerver is
              // `tickets.view`-ra kapuzza) — nem kell hozzá `tickets.manage`.
              canReact={currentSub !== null}
            />
          ))}
        </ul>
      )}

      {/* RAGADÓS COMPOSER a szál alján. `sticky bottom-0` + ÁTLÁTSZATLAN háttér: a szál mögötte görög el,
          a sáv viszont a görgetés-port alján marad, tehát az írás nulla görgetésből indul. A `-mx-*`/`px-*`
          pár azért kell, hogy a sáv háttere a lap-tartalom SZÉLÉIG fusson (különben a kommentek a sáv
          mellett átlátszanának). */}
      {canManage && !composerOpen && (
        <div className={STICKY_BAR}>
          <button
            type="button"
            onClick={() => setComposerOpen(true)}
            className="border-border bg-card text-muted-foreground hover:border-brand hover:text-foreground ease-atlas flex w-full items-center gap-2 rounded-lg border px-3 py-2 text-left text-[13px] transition-colors duration-150"
          >
            <ChatBubbleLeftRightIcon className="size-4 shrink-0" aria-hidden="true" />
            <span className="truncate">
              {!blank
                ? t('drafts.resume')
                : kind === 'external'
                  ? t('tickets.comments.composerCollapsedExternal')
                  : t('tickets.comments.composerCollapsedInternal')}
            </span>
          </button>
        </div>
      )}

      {canManage && composerOpen && (
        <form onSubmit={onSubmit} className={cn(STICKY_BAR, 'space-y-3 py-3')}>
          {/* MINDKÉT fajta a GLOBÁLIS WYSIWYG-gel (user-döntés, 2026-08-25): a belső jegyzet is kap formázást,
              linket, képet. A szerver mindkettőt szanitálja (a belső eddig érintetlenül került a sorba — most,
              hogy HTML jön, a szanitálás kötelező: `tickets-comments.tx.ts` `storedBody`). */}
          <RichTextEditor
            imageAccountId={accountId}
            value={body}
            onChange={updateBody}
            // FAJTÁNKÉNTI, RÖVID placeholder: a komment kimegy a partnerhez, a megjegyzés nem — a
            // megnevezés tehát nem mindegy (user-döntés).
            placeholder={
              kind === 'external'
                ? t('tickets.comments.placeholderExternal')
                : t('tickets.comments.placeholderInternal')
            }
            aria-label={t('tickets.comments.bodyLabel')}
            autoFocus
          />
          {/* EGY AKCIÓ-SOR a szerkesztő alatt (user-döntés, 2026-08-25): BALRA a sablon-műveletek (betöltés +
              mentés sablonként, mindkettő ikonos), JOBBRA a küldés. Eddig a sablon-betöltő a szerkesztő FÖLÖTT,
              külön sávban állt — két akció-sáv egy composerhez fölösleges volt. */}
          <div className="flex flex-wrap items-center gap-2">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button type="button" size="sm" variant="outline" disabled={templates.length === 0}>
                  <DocumentDuplicateIcon />
                  {t('tickets.comments.loadTemplate')}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="start" className="max-h-72 overflow-y-auto">
                {templates.map((tpl) => (
                  <DropdownMenuItem key={tpl.id} onSelect={() => loadTemplate(tpl.content)}>
                    <span className="truncate">{tpl.title}</span>
                  </DropdownMenuItem>
                ))}
              </DropdownMenuContent>
            </DropdownMenu>
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={blank}
              onClick={() => setSaveTplOpen(true)}
            >
              <BookmarkSquareIcon />
              {t('tickets.comments.saveAsTemplate')}
            </Button>
            <div className="ml-auto flex items-center gap-2">
              <DraftStatus
                savedAt={draft.savedAt}
                onDiscard={() => {
                  draft.discard();
                  setBody('');
                }}
                disabled={createMutation.isPending}
              />
              {/* a belső jegyzet nem megy ki senkinek, csak a jegyre kerül: „Mentés" + lakat (a jegylista
                  belső-jegyzet jele), a külső válasz „Küldés" + papírrepülő (user-lelet) */}
              <Button type="submit" variant="brand" size="sm" disabled={!canSend}>
                {kind === 'internal' ? <LockClosedIcon /> : <PaperAirplaneIcon />}
                {kind === 'internal'
                  ? createMutation.isPending
                    ? t('tickets.comments.savingNote')
                    : t('tickets.comments.saveNote')
                  : createMutation.isPending
                    ? t('tickets.comments.sending')
                    : t('tickets.comments.send')}
              </Button>
            </div>
          </div>
        </form>
      )}

      {/* „Mentés sablonként" dialog — a jelenlegi komment-szöveg mentése cím alá (updateOrCreate a title-re). */}
      <SaveTemplateDialog
        open={saveTplOpen}
        onOpenChange={setSaveTplOpen}
        initialContent={body}
        pending={templateMutation.isPending}
        onSave={(title, content) =>
          templateMutation.mutate({ title, content }, { onSuccess: () => setSaveTplOpen(false) })
        }
      />
    </section>
  );
}

/** Egy komment-kártya — a szerzőnek inline szerkeszthető (kind + body; If-Match/OCC). */
function CommentItem({
  comment: c,
  ticketId,
  accountId,
  locale,
  canEdit,
  canReact,
}: {
  comment: TicketComment;
  ticketId: string;
  /** A ticket fiókja — a szerkesztésbe beszúrt kép cél-fiókja. */
  accountId: string;
  locale: string;
  canEdit: boolean;
  /** Reagálhat-e a hívó (a lap `tickets.view`-ja) — jog nélkül a sáv CSAK olvasható. */
  canReact: boolean;
}): JSX.Element {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(c.body);
  const [draftKind, setDraftKind] = useState<CommentKind>(c.kind);

  // A szerver-állapot változásakor (mentés → új version) a szerkesztő-piszkozat visszaáll a mentettre.
  useEffect(() => {
    setDraft(c.body);
    setDraftKind(c.kind);
    setEditing(false);
  }, [c.id, c.version, c.body, c.kind]);

  const mutation = useEntityMutation<{ kind: CommentKind; body: string }>({
    mutationFn: (v) =>
      api.ticketCommentUpdate.mutation({
        params: { id: ticketId, commentId: c.id },
        body: v,
        headers: { 'if-match': String(c.version) },
      }),
    invalidateKeys: [ticketKeys.comments(ticketId), ticketKeys.detail(ticketId)],
    messages: {
      success: t('toast:tickets.commentUpdated'),
      conflict: t('toast:tickets.conflict'),
      forbidden: t('toast:tickets.forbidden'),
      validation: t('toast:tickets.validation'),
      error: t('toast:tickets.error'),
    },
  });

  const canSave = !isBodyBlank(draft) && !mutation.isPending;

  /**
   * REAKCIÓ-TOGGLE — OPTIMISTA: a sáv AZONNAL átrajzolódik, hiba esetén visszaáll.
   *
   * A `mutationFn` a 4xx-et DOBJA. Ez nem stílus-kérdés: a ts-rest react-query mutációja a 4xx-et NEM
   * dobja (a promise a `{ status, body }`-vel RESOLVE-ol), tehát az `onError` sosem futna — mérve élő
   * stacken: egy 404-re a chip csendben visszaállt, hibajelzés NÉLKÜL. Ugyanez a csapda van
   * leírva a közös `use-entity-mutation.ts` fejlécében; azt a hookot itt azért nem használjuk, mert az
   * optimista `update` nem kapja meg a mutáció VÁLTOZÓJÁT (nekünk az emoji kell hozzá).
   */
  const reactionMutation = useMutation({
    mutationFn: async (emoji: string) => {
      const res = await api.ticketCommentReactionToggle.mutation({
        params: { id: ticketId, commentId: c.id },
        body: { emoji: emoji as (typeof REACTION_CHOICES)[number] },
      });
      if (res.status >= 400) throw new Error(`reaction-toggle:${String(res.status)}`);
      return res;
    },
    onMutate: async (emoji: string) => {
      const key = ticketKeys.comments(ticketId);
      await queryClient.cancelQueries({ queryKey: key });
      const snapshot = queryClient.getQueryData(key);
      queryClient.setQueryData(key, (prev: unknown) => toggleInCache(prev, c.id, emoji));
      return { snapshot, key };
    },
    onError: (_e, _v, ctx) => {
      if (ctx) queryClient.setQueryData(ctx.key, ctx.snapshot);
      toast.error(t('toast:tickets.error'));
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ticketKeys.comments(ticketId) });
    },
  });

  return (
    <li
      className={cn(
        // `group/comment` + `relative`: a reakció-hozzáadás gombja a kártya SZEGÉLYÉN ül, és a kártya
        // hoverére/fókuszára jelenik meg (lásd `TicketCommentReactions` `addPlacement="edge"`).
        'group/comment relative rounded-lg border p-3',
        // A belső jegyzet halvány warning-tónusú háttérrel jól elkülönül a kommenttől. A KÜLSŐ kommentnél a
        // „mi vs. ügyfél" válik el (Évi #84): a munkatárs kommentje a portál „saját" tónusát kapja, az ügyfélé
        // kártya-hátteret — korábban mindkettő a lap hátterével szinte egyező `bg-sunken/40` volt, és összefolyt.
        c.kind === 'internal'
          ? 'border-warning/30 bg-warning-soft/40'
          : c.authorIsColleague
            ? THREAD_TONE_OURS
            : 'border-border bg-card',
      )}
    >
      <div className="mb-1.5 flex flex-wrap items-center gap-2">
        {/* SZERZŐ: a KÖZÖS `PersonChip` — profilkép (monogram-fallback) + NYELVHELYES névsorrend (magyarul
            vezetéknév elöl, más nyelven keresztnév elöl). Korábban itt nyers `Avatar` + `authorName` állt,
            tehát a névsorrend a szerver `displayName`-jét követte, nem a felület nyelvét — a support-csapat
            lista tagok-oszlopával sem egyezett (user-lelet, 2026-08-31). */}
        <div className="text-md min-w-0 font-medium">
          <PersonChip
            name={
              c.authorName ??
              // Van szerző-id, csak nem oldható fel → TÖRÖLT személy; id nélkül marad az „Ismeretlen szerző".
              (c.authorIdentityId ? t('common.deletedUser') : t('tickets.comments.unknownAuthor'))
            }
            givenName={c.authorGivenName}
            familyName={c.authorFamilyName}
            avatarUrl={c.authorAvatarUrl ?? null}
            accentColor={c.authorTeamColor ?? null}
            size="sm"
          />
        </div>
        {/* NINCS fajta-BADGE a soron (user-döntés, 2026-09-09): a fül-sáv már FAJTÁRA SZŰR — egy fülön
            belül minden sor ugyanaz a fajta —, a belső jegyzetet pedig a kártya tónusa (warning-soft
            háttér + szegély) amúgy is elkülöníti. A badge így soronként ugyanazt mondta harmadszor. */}
        <span className="ml-auto flex items-center gap-2">
          <EditedMarker editedAt={c.editedAt} />
          <Text variant="secondary" as="span">
            {fmtDateTime(c.createdAt, locale)}
          </Text>
        </span>
      </div>

      {editing ? (
        <div className="space-y-2">
          {/* NINCS fajta-VÁLASZTÓ a szerkesztőben (user-döntés, 2026-09-09): a szál FÜLE már megmondja,
              hol vagyunk, és egy MÁR LEADOTT komment fajtájának átírása nem szerkesztés, hanem
              újra-címzés — ha egy komment belső jegyzetté válhatna (vagy fordítva), a partner
              láthatósága utólag változna meg. A mentés a MEGLÉVŐ fajtát küldi vissza. */}
          {/* MINDKÉT fajta RICH-TEXT (a composerrel egyezően). Az a11y-NÉV KÜLÖNBÖZŐ: a lapon egyszerre ott a
              composer és ez a szerkesztő — két azonos nevű `textbox` a képernyőolvasóban (és a tesztben)
              megkülönböztethetetlen volt. */}
          <RichTextEditor
            imageAccountId={accountId}
            value={draft}
            onChange={setDraft}
            aria-label={t('tickets.comments.editBodyLabel')}
          />
          <div className="flex justify-end gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              disabled={mutation.isPending}
              onClick={() => {
                setDraft(c.body);
                setDraftKind(c.kind);
                setEditing(false);
              }}
            >
              {t('tickets.cancel')}
            </Button>
            <Button
              type="button"
              variant="brand"
              size="sm"
              disabled={!canSave}
              onClick={() =>
                mutation.mutate(
                  { kind: draftKind, body: draft.trim() },
                  { onSuccess: () => setEditing(false) },
                )
              }
            >
              {mutation.isPending ? t('tickets.saving') : t('tickets.save')}
            </Button>
          </div>
        </div>
      ) : looksLikeStoredHtml(c.body) ? (
        // RICH-TEXT törzs (mindkét fajta) — DOMPurify-sanitizálva (stored-XSS fal).
        <RichTextContent html={c.body} />
      ) : (
        // MIGRÁLT (legacy) sima szöveg — sortörés-megőrzéssel. Egy `RichTextContent` itt összeolvasztaná a
        // sortöréseket (a `<br>`/`<p>` hiányzik a tárolt alakból).
        <p className="whitespace-pre-line text-sm">{c.body}</p>
      )}
      {/* A KÁRTYA SZEGÉLYÉRE ülő AKCIÓ-SOR: a reakció-chipek, a reakció-hozzáadás és a SZERKESZTÉS
          egy helyen, a jobb-alsó sarokban, a szegélyt „átlépve" (user-döntés, 2026-09-09). MIÉRT ide:
          (a) a chipek így nem tolják szét a kártya tartalmát, (b) az akciók egy csoportban vannak, ahol a
          szem keresi őket, (c) MINDIG látszanak — mobilon nincs hover, és a rejtett akcióról a felhasználó
          nem tudja, hogy létezik. A szerkesztés IKON-ONLY, a felirat tooltipben. */}
      {/* SZERKESZTÉS ALATT AZ EGÉSZ AKCIÓ-SOR ELTŰNIK — nem csak a ceruza (user-döntés, 2026-09-09). A
          reakció-chipek a kártya SZEGÉLYÉN ülnek, a szerkesztő pedig épp ott nyit mentés/mégsem gombokat:
          a két vezérlő-csoport egymásra lapolt, és a „most mit szerkesztek" kérdés elmosódott. A reakció
          amúgy sem a szerkesztés része — a piszkozatra reagálni értelmetlen. */}
      {!editing && (
        <div className="absolute -bottom-3 right-4 z-10 flex items-center gap-1.5">
          <TicketCommentReactions
            reactions={c.reactions}
            canReact={canReact}
            pending={reactionMutation.isPending}
            onToggle={(emoji) => reactionMutation.mutate(emoji)}
          />
          {canEdit && (
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  onClick={() => setEditing(true)}
                  aria-label={t('tickets.comments.edit')}
                  className="border-border bg-card text-muted-foreground hover:border-brand hover:text-foreground ease-atlas flex items-center rounded-full border p-1.5 shadow-sm transition-colors duration-150"
                >
                  <PencilSquareIcon className="size-3.5" />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top">{t('tickets.comments.edit')}</TooltipContent>
            </Tooltip>
          )}
        </div>
      )}
    </li>
  );
}

/** „Mentés sablonként" dialog — cím + tartalom (a komment-szövegből előtöltve). */
function SaveTemplateDialog({
  open,
  onOpenChange,
  initialContent,
  pending,
  onSave,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  initialContent: string;
  pending: boolean;
  onSave: (title: string, content: string) => void;
}): JSX.Element {
  const { t } = useTranslation();
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');

  useEffect(() => {
    if (open) {
      setTitle('');
      setContent(initialContent);
    }
  }, [open, initialContent]);

  const canSave = title.trim() !== '' && content.trim() !== '' && !pending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('tickets.comments.saveTemplateTitle')}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          <div className="space-y-1.5">
            <Text variant="overline" as="label">
              {t('tickets.comments.templateTitleLabel')}
            </Text>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={255}
              aria-label={t('tickets.comments.templateTitleLabel')}
            />
          </div>
          <div className="space-y-1.5">
            <Text variant="overline" as="label">
              {t('tickets.comments.templateContentLabel')}
            </Text>
            <Textarea
              value={content}
              onChange={(e) => setContent(e.target.value)}
              rows={5}
              maxLength={20000}
              aria-label={t('tickets.comments.templateContentLabel')}
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={pending}
          >
            {t('tickets.cancel')}
          </Button>
          <Button
            type="button"
            variant="brand"
            disabled={!canSave}
            onClick={() => onSave(title, content)}
          >
            {pending ? t('tickets.saving') : t('tickets.save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
